import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ReactComponent as DropDownIcon } from 'Assets/svg/dropdown-icon.svg';
import { ReactComponent as ContactEmailIcon } from 'Assets/svg/contact-email.svg';
import DotIcon from 'Assets/png/chart-dot.png';
import { LoadingWrapper } from 'Components/common/loadingWrapper';
import { userDetail } from 'Stores/userDetail';
import profileState from 'Stores/profile';
import * as CONSTANTS from 'Utils/constants';
import { Utils } from 'Utils/utils';
import { toasterState } from 'Components/common/toaster';
import EmptyState from './emptyState';
import './usageAnalytics.scss';
import TeamMembersDropdown from './teamMembersDropdown/teamMembersDropdown';
import EmailPopUp from './EmailPopUp/EmailPopUp';
import ManageRecurringEmails from './manageRecurringEmailsPopUp/manageRecurringEmailsPopUp';
import CustomRangeSelection from './customRangeSelection';
import APIUsageChart from './APIUsageChart';

const barChartColors = [
  '#F5AAA1',
  '#9DB6FB',
  '#92DFA4',
  '#F3A1C9',
  '#A1E5E3',
  '#D0B8F0',
  '#DFAD7F',
  '#ADBCCC',
  '#61B7B9',
  '#A08CBD',
  '#709CD9',
  '#F4CE90',
  '#95CDF4',
  '#C98789',
  '#A8A8A8',
];

const teamMemberLimit = 15;

function getPreviousMonthData(date, length) {
  return Array.from({ length }).reduce((result, item, index) => {
    const previousDate = new Date(
      date.getFullYear(),
      date.getMonth() - index,
      1,
    );
    const createdDate = new Date(userDetail.userInfo.creationTimeStamp);
    if (
      previousDate <
      new Date(createdDate.getFullYear(), createdDate.getMonth(), 1)
    )
      return result;
    return [
      `${
        CONSTANTS.monthNames[previousDate.getMonth()]
      } ${previousDate.getFullYear()}`,
      ...result,
    ];
  }, []);
}

function getMonthRange(monthArray) {
  return {
    startMonth: monthArray[0].split(' ')[0],
    startYear: monthArray[0].split(' ')[1],
    endMonth: monthArray[monthArray.length - 1].split(' ')[0],
    endYear: monthArray[monthArray.length - 1].split(' ')[1],
  };
}

function getBackgroundGradient(height) {
  const chart = document.createElement('canvas').getContext('2d');
  const gradient = chart.createLinearGradient(0, 0, 0, height);

  gradient.addColorStop(0, 'rgba(0, 215, 181, 0.85)');
  gradient.addColorStop(0.3, 'rgba(0, 215, 181, 0.6)');
  gradient.addColorStop(0.7, 'rgba(0, 215, 181, 0.25)');
  gradient.addColorStop(1, 'rgba(0, 215, 181, 0.1)');

  return gradient;
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels,
);

const lineChartoptions = {
  tension: 0.3,
  responsive: true,
  maintainAspectRatio: false,
  clip: false,
  scales: {
    x: {
      offset: true,
      grid: {
        display: false,
      },
      ticks: {
        color: 'rgb(28, 39, 94)',
        callback(value) {
          return `${this.getLabelForValue(value)
            .split(' ')
            .reduce((output, item, index) => {
              if (index === 0) return item.slice(0, 3);
              return `${output} ${item}`;
            }, '')}`;
        },
      },
    },
    y: {
      ticks: {
        maxTicksLimit: 6,
        color: 'rgb(28, 39, 94)',
        padding: 10,
      },
      beginAtZero: true,
      grid: {
        drawBorder: false,
      },
    },
  },
  elements: {
    point: {
      pointStyle: () => {
        const img = new Image();
        img.width = 12;
        img.height = 12;
        img.src = DotIcon;
        return img;
      },
    },
  },
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      display: false,
      position: 'top',
    },
  },
};

const APILineChartOptions = Utils.copyObjectWithoutReference(lineChartoptions);

APILineChartOptions.hover = { intersect: false };
APILineChartOptions.scales.x.ticks.maxTicksLimit = 8;
APILineChartOptions.elements.point.radius = 0;
APILineChartOptions.plugins.tooltip = {
  mode: 'index',
  intersect: false,
};

const barChartTooltip = context => {
  console.log(context);
};

const barChartOptions = {
  responsive: true,
  categoryPercentage: 1,
  datasets: {
    bar: {
      borderRadius: 4,
      barPercentage: 0.7,
      maxBarThickness: 15,
      skipNull: true,
    },
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      offset: true,
      ticks: {
        color: 'rgb(28, 39, 94)',
        callback(value) {
          return `${this.getLabelForValue(value)
            .split(' ')
            .reduce((output, item, index) => {
              if (index === 0) return item.slice(0, 3);
              return `${output} ${item}`;
            }, '')}`;
        },
      },
      grid: {
        display: false,
      },
    },
    y: {
      ticks: {
        maxTicksLimit: 6,
        color: 'rgb(28, 39, 94)',
        padding: 10,
      },
      grid: {
        drawBorder: false,
      },
    },
  },
  plugins: {
    datalabels: {
      display: false,
    },
    legend: {
      position: 'bottom',
      labels: {
        padding: 20,
        boxWidth: 15,
        boxHeight: 10,
        color: 'rgb(28, 39, 94)',
        font: {
          size: 13,
          lineHeight: 1.33,
        },
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      external: barChartTooltip,
    },
  },
};

const UsageAnalytics = observer(() => {
  const {
    noOfSeatsEligible,
    noOfSeatsUsed,
    memberSubscription,
    isEnterpriseAdmin,
    lbCredits,
    phoneCreditsRemaining,
    enrichmentCreditsRemaining,
  } = userDetail.userInfo;

  const [currentTab, setCurrentTab] = useState(0);
  const mailsDropDown = [
    { label: 'One-Time Email' },
    { label: 'Email Digest' },
    { label: 'Manage Email Digest' },
  ];
  const monthsDropDown = [
    { label: 'Current Month' },
    { label: 'Current + Last 1 Month' },
    { label: 'Current + Last 2 Months' },
    { label: 'Custom Date Range ' },
  ];
  const [showTeamMembersDropdown, setShowTeamMembersDropdown] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mailsDropDownPopUp, setMailsDropDownPopUp] = useState(false);
  const [mailValue, setMailValue] = useState(null);
  const manageEmailRef = useRef(null);
  const isFirstRun = useRef(false);
  const canvasWrapRef = useRef();
  const deletePopRef = useRef(null);
  const emailDropDownRef = useRef(null);
  const currentDate = new Date();
  const [clickedIndex, setClickedIndex] = useState(null);
  const [monthRange, setMonthRange] = useState(
    getMonthRange(getPreviousMonthData(currentDate, 10)),
  );

  const memberDropdownRef = useRef();

  const selectedStartDate = new Date(
    monthRange.startYear,
    CONSTANTS.monthNames.indexOf(monthRange.startMonth),
  );

  const selectedEndDate = new Date(
    monthRange.endYear,
    CONSTANTS.monthNames.indexOf(monthRange.endMonth),
  );

  const monthRangeData = getPreviousMonthData(
    selectedEndDate,
    selectedEndDate.getMonth() -
      selectedStartDate.getMonth() +
      1 +
      12 * (selectedEndDate.getFullYear() - selectedStartDate.getFullYear()),
  );

  const getTeamMembers = async () => {
    const response = await profileState.fetchTeamMembers();
    setTeamMembers(
      response.data.members
        ? response.data.members
            .reverse()
            .filter(
              member =>
                member.status !== 'INACTIVE' && member.status !== 'INVITED',
            )
            .concat(
              response.data.members.filter(
                member => member.status === 'INVITED',
              ),
              response.data.members.filter(
                member => member.status === 'INACTIVE',
              ),
            )
        : [],
    );
  };

  const fetchUsageAnalytics = async range => {
    setIsLoading(true);
    await profileState.getUsageAnalyticsData(range || monthRange);
    setIsLoading(false);
  };

  const getMonthRangeFromDropDown = async monthValue => {
    let monthsObj = {};
    const date = new Date();
    switch (monthValue) {
      case monthsDropDown[0].label: {
        monthsObj = {
          endMonth: CONSTANTS.monthNames[date.getMonth()],
          endYear: date.getFullYear(),
          startMonth: CONSTANTS.monthNames[date.getMonth()],
          startYear: date.getFullYear(),
        };
        break;
      }
      case monthsDropDown[1].label: {
        monthsObj = getMonthRange(getPreviousMonthData(date, 2));
        break;
      }
      case monthsDropDown[2].label: {
        monthsObj = getMonthRange(getPreviousMonthData(date, 3));
        break;
      }
      default:
        break;
    }
    setMonthRange(monthsObj);
    fetchUsageAnalytics(monthsObj);
  };

  const getDataForAdmin = async () => {
    setIsLoading(true);
    const getTeamMemberPromise = getTeamMembers();
    const fetchAnalyticsPromise = profileState.getUsageAnalyticsData(
      monthRange,
    );
    Promise.allSettled([getTeamMemberPromise, fetchAnalyticsPromise]).then(
      () => {
        setIsLoading(false);
      },
    );
  };

  function handleClickOutside(event) {
    if (
      manageEmailRef.current &&
      !manageEmailRef.current?.contains(event.target) &&
      manageEmailRef !== null &&
      !deletePopRef?.current?.contains(event?.target)
    ) {
      setClickedIndex(null);
      setMailValue(null);
    }

    if (
      emailDropDownRef.current &&
      !emailDropDownRef.current?.contains(event.target) &&
      mailsDropDownPopUp
    )
      setMailsDropDownPopUp(false);
  }
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (mailValue !== null) {
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        // eslint-disable-next-line no-undef
        document.removeEventListener('click', handleClickOutside, true);
      };
    }

    if (mailsDropDownPopUp) {
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        // eslint-disable-next-line no-undef
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [mailValue, mailsDropDownPopUp]);

  function handleMemberDropdownClickOutside(event) {
    if (
      memberDropdownRef.current &&
      !memberDropdownRef.current?.contains(event.target) &&
      showTeamMembersDropdown
    )
      setShowTeamMembersDropdown(false);
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (showTeamMembersDropdown) {
      // eslint-disable-next-line no-undef
      document.addEventListener(
        'click',
        handleMemberDropdownClickOutside,
        true,
      );
      return () => {
        // eslint-disable-next-line no-undef
        document.removeEventListener(
          'click',
          handleMemberDropdownClickOutside,
          true,
        );
      };
    }
  }, [showTeamMembersDropdown]);

  const handleOnClose = (message, flag, classNameProperty = '') => {
    if (message !== '')
      toasterState.setToastMsg(message, flag, { className: classNameProperty });
    setMailValue(null);
    setClickedIndex(null);
  };

  useEffect(() => {
    isFirstRun.current = true;
    if (isEnterpriseAdmin && Utils.isCreditBasedSubscription())
      getDataForAdmin();
    else fetchUsageAnalytics();
  }, [isEnterpriseAdmin]);

  useEffect(() => {
    if (!isFirstRun.current) fetchUsageAnalytics();
    isFirstRun.current = false;
  }, [currentTab]);

  useEffect(() => {
    Utils.mixpanelTrack(
      CONSTANTS.MX_EMAIL_DIGEST_EVENTS.USAGE_ANALYTICS_VIEWED,
    );
  }, []);

  const handleOnClikcMailsDropDown = () => {
    setMailsDropDownPopUp(!mailsDropDownPopUp);
  };

  return (
    <div className="usage-analytics-page">
      <div className="credits-card">
        <div className="top-wrap">
          <div className="page-title">Usage Overview</div>
          <div className="billing-cycle">
            {profileState.usageAnalyticsData.subscriptionStartDate &&
            profileState.usageAnalyticsData.subscriptionRenewalDate
              ? `Billing Cycle: 
            ${Utils.getFormattedDate(
              profileState.usageAnalyticsData.subscriptionStartDate,
            )} - ${Utils.getFormattedDate(
                  profileState.usageAnalyticsData.subscriptionRenewalDate,
                )}`
              : ''}
          </div>
        </div>
        <div className="credit-values">
          {noOfSeatsEligible > 1 && (
            <div className="title-with-value">
              <span className="title">Seats Used</span>
              <span className="value">{`${noOfSeatsUsed}/${noOfSeatsEligible}`}</span>
            </div>
          )}
          {!Utils.isCreditBasedSubscription() && (
            <>
              <div className="title-with-value">
                <span className="title">Views Remaining</span>
                <span className="value">
                  {Utils.numberFormat(
                    profileState.usageAnalyticsData.views || 0,
                  )}
                </span>
              </div>
              <div className="title-with-value">
                <span className="title">Downloads Remaining</span>
                <span className="value">
                  {Utils.numberFormat(
                    profileState.usageAnalyticsData.downloads || 0,
                  )}
                </span>
              </div>
            </>
          )}
          {Utils.isCreditBasedSubscription() && (
            <div className="title-with-value">
              <span className="title">Email Credits Remaining</span>
              <span className="value">
                <span>{Utils.numberFormat(lbCredits)}</span>
              </span>
            </div>
          )}
          {Utils.isCreditBasedSubscription() && (
            <div className="title-with-value">
              <span className="title">Phone Credits Remaining</span>
              <span className="value">
                <span>{Utils.numberFormat(phoneCreditsRemaining)}</span>
              </span>
            </div>
          )}
          {Utils.isCrmEnrichmentEnabled() && (
            <div className="title-with-value">
              <span className="title">Enrichment Credits Remaining</span>
              <span className="value">
                {Utils.numberFormat(enrichmentCreditsRemaining)}
              </span>
            </div>
          )}
          <div className="title-with-value">
            <span className="title">Monthly Credit Limit</span>
            <span className="value">
              {profileState.usageAnalyticsData.totalLbCredits !== -1
                ? profileState.usageAnalyticsData.totalLbCredits
                  ? Utils.numberFormat(
                      profileState.usageAnalyticsData.totalLbCredits,
                    )
                  : '-'
                : 'No Limit Set'}
            </span>
          </div>
        </div>
      </div>

      {Utils.isCreditBasedSubscription() && (
        <div className="chart-wrapper">
          <div className="chart-tooltip" />
          <LoadingWrapper position="fixed" show={isLoading} />
          <div className="top-wrapper">
            <div className="title-text">Credits Usage</div>
            <div className="dropdowns-for-chart">
              <div className="dropdowns-wrapper">
                {isEnterpriseAdmin && (
                  <div className="tab-wrapper">
                    <span
                      className={currentTab === 0 ? 'active' : ''}
                      onClick={() => {
                        setMonthRange(
                          getMonthRange(getPreviousMonthData(currentDate, 10)),
                        );
                        setCurrentTab(0);
                      }}
                    >
                      Overall Team
                    </span>
                    <span
                      ref={memberDropdownRef}
                      className={`team-member ${
                        currentTab === 1 ? ' active' : ''
                      }`}
                      onClick={async () => {
                        if (
                          (teamMembers.length > teamMemberLimit &&
                            selectedTeamMembers.length === 0) ||
                          currentTab === 1
                        ) {
                          setShowTeamMembersDropdown(true);
                        } else {
                          setSelectedTeamMembers(
                            selectedTeamMembers.length > 0
                              ? selectedTeamMembers
                              : teamMembers,
                          );
                          setMonthRange(
                            getMonthRange(getPreviousMonthData(currentDate, 6)),
                          );
                          setCurrentTab(1);
                        }
                      }}
                    >
                      Team Members
                      <DropDownIcon />
                      {showTeamMembersDropdown && (
                        <TeamMembersDropdown
                          isLoading={isLoading}
                          teamMemberLimit={teamMemberLimit}
                          selectedTeamMembers={selectedTeamMembers}
                          teamMembers={teamMembers}
                          onApply={data => {
                            setSelectedTeamMembers(data);
                            setCurrentTab(1);
                            setMonthRange(
                              getMonthRange(
                                getPreviousMonthData(currentDate, 6),
                              ),
                            );
                          }}
                          onClose={() => setShowTeamMembersDropdown(false)}
                        />
                      )}
                    </span>
                  </div>
                )}
                <CustomRangeSelection
                  fetchUsageAnalytics={fetchUsageAnalytics}
                  getMonthRangeFromDropDown={getMonthRangeFromDropDown}
                  isEnterpriseAdmin={isEnterpriseAdmin}
                  currentTab={currentTab}
                  getPreviousMonthData={getPreviousMonthData}
                  getMonthRange={getMonthRange}
                />
              </div>
              {isEnterpriseAdmin === true && (
                <div
                  className="months-drop-down custom-date-range"
                  ref={emailDropDownRef}
                >
                  <span onClick={handleOnClikcMailsDropDown}>
                    <ContactEmailIcon className="contact-email-icon" />
                    <div>Email report</div>
                  </span>
                  {mailsDropDownPopUp === true && (
                    <div className="custom-data-range-options mails-drop-down">
                      {mailsDropDown.map((ele, i) => {
                        return (
                          <div
                            onClick={() => {
                              setMailValue(ele.label);
                              setMailsDropDownPopUp(false);
                            }}
                            key={i}
                          >
                            {ele.label}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {mailValue !== null &&
                    clickedIndex === null &&
                    mailValue !== 'Manage Email Digest' && (
                      <EmailPopUp
                        ref={manageEmailRef}
                        value={mailValue}
                        onClose={handleOnClose}
                        index={null}
                      />
                    )}

                  {clickedIndex !== null && (
                    <EmailPopUp
                      value="Email Digest"
                      ref={manageEmailRef}
                      index={clickedIndex}
                      onClose={handleOnClose}
                    />
                  )}
                  {mailValue !== null && mailValue === 'Manage Email Digest' && (
                    <ManageRecurringEmails
                      onClose={handleOnClose}
                      deletePopRef={deletePopRef}
                      manageRef={manageEmailRef}
                      openRecurringEmail={async (index, value) => {
                        await setMailValue('Email Digest');
                        await setClickedIndex(index);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="chart-container" ref={canvasWrapRef}>
            {!isLoading ? (
              profileState.usageAnalyticsData.data.length > 0 &&
              !profileState.usageAnalyticsData.data?.every(
                usageData =>
                  usageData.credits === 0 || usageData.credits === null,
              ) ? (
                isEnterpriseAdmin &&
                (currentTab === 1 || monthRangeData.length === 1) ? (
                  <Bar
                    options={{
                      ...barChartOptions,
                      categoryPercentage:
                        selectedTeamMembers.length > 5
                          ? 0.75
                          : selectedTeamMembers.length * 0.1 + 0.2,
                    }}
                    data={{
                      labels: monthRangeData,
                      datasets: profileState.getTeamBarChartData({
                        rangeData: monthRangeData,
                        rangeLength: monthRangeData.length,
                        barChartColors,
                        memberData:
                          selectedTeamMembers.length > 0
                            ? selectedTeamMembers
                            : teamMembers,
                      }),
                    }}
                  />
                ) : (
                  <Line
                    options={lineChartoptions}
                    data={{
                      labels: monthRangeData,
                      datasets: [
                        {
                          label: 'Credits used',
                          data: isEnterpriseAdmin
                            ? profileState.getAdminOverallAnalyticsData(
                                monthRangeData,
                                monthRangeData.length,
                              )
                            : profileState.getTeamMemberAnalyticsData(
                                monthRangeData,
                                monthRangeData.length,
                              ),
                          borderColor: '#00D7B5',
                          backgroundColor: getBackgroundGradient(
                            canvasWrapRef.current
                              ? canvasWrapRef.current.clientHeight
                              : 400,
                          ),
                          fill: true,
                        },
                      ],
                    }}
                  />
                )
              ) : (
                <EmptyState chart={currentTab === 1 ? 'bar' : 'line'} />
              )
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
});

export default UsageAnalytics;
